import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

type Program = 'bcd' | 'thor' | 'virtuoso';

interface Opportunity {
  client_name: string;
  account_number: string | null;
  property_key: string;
  property_name: string;
  chain: string | null;
  city: string | null;
  state: string | null;
  reservations: number;
  nights: number;
  room_spend: number;
  adr: number | null;
  program_mix: Record<Program, number>;
  dominant_program: Program;
  commissionable: number;
  non_commissionable: number;
  commission_unknown: number;
}

interface OpportunityResult {
  window: { from: string; to: string; months: number };
  opportunities: Opportunity[];
  totals: {
    clusters: number;
    reservations: number;
    nights: number;
    room_spend: number;
    commission_unknown: number;
  };
  truncated: boolean;
}

const PROGRAM_LABEL: Record<Program, string> = {
  bcd: 'BCD',
  thor: 'Thor',
  virtuoso: 'Virtuoso',
};
const MONTH_OPTIONS = [6, 12, 24];
const MIN_NIGHTS_OPTIONS = [
  { value: 0, label: 'Any' },
  { value: 10, label: '10+' },
  { value: 25, label: '25+' },
  { value: 50, label: '50+' },
];

export default function RateOpportunitiesPage() {
  const [months, setMonths] = useState(12);
  const [program, setProgram] = useState<'' | Program>('');
  const [minNights, setMinNights] = useState(10);
  const [search, setSearch] = useState('');
  const [data, setData] = useState<OpportunityResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      months: String(months),
      limit: '500',
    });
    if (program) params.set('program', program);
    apiFetch<OpportunityResult>(`/api/btp/rate-opportunities?${params}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [months, program]);

  // min-nights + client/hotel search are applied client-side over the loaded set.
  const rows = useMemo(() => {
    const all = data?.opportunities ?? [];
    const term = search.trim().toLowerCase();
    return all.filter(
      (o) =>
        o.nights >= minNights &&
        (term === '' ||
          o.client_name.toLowerCase().includes(term) ||
          o.property_name.toLowerCase().includes(term) ||
          (o.chain ?? '').toLowerCase().includes(term)),
    );
  }, [data, minNights, search]);

  const totals = useMemo(() => {
    const nights = rows.reduce((s, o) => s + o.nights, 0);
    const spend = rows.reduce((s, o) => s + o.room_spend, 0);
    const unknown = rows.reduce((s, o) => s + o.commission_unknown, 0);
    const reservations = rows.reduce((s, o) => s + o.reservations, 0);
    return { clusters: rows.length, nights, spend, unknown, reservations };
  }, [rows]);

  const unknownPct =
    totals.reservations > 0
      ? Math.round((totals.unknown / totals.reservations) * 100)
      : 0;

  return (
    <div className="page" data-testid="rate-opportunities-page">
      <div className="page-header">
        <h1>Rate Opportunities</h1>
        <p className="page-intro">
          Hotels the client base books heavily on a third-party consortium rate
          (BCD, Thor, Virtuoso) rather than a rate Andavo negotiated. Each row
          is a candidate to negotiate our own rate — cheaper for the client,
          commissionable for us. Ranked by nights booked on consortium rates
          over the trailing {months} months.
        </p>
      </div>

      <div className="kpi-row kpi-row-4">
        <div className="kpi-card kpi-highlight">
          <span className="kpi-label">Opportunities</span>
          <span className="kpi-value">{totals.clusters.toLocaleString()}</span>
          <span className="kpi-sub">client + hotel clusters</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Nights on consortium</span>
          <span className="kpi-value">{totals.nights.toLocaleString()}</span>
          <span className="kpi-sub">across this slice</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Room spend</span>
          <span className="kpi-value">{fmtMoneyShort(totals.spend)}</span>
          <span className="kpi-sub">nightly charges, {months}mo</span>
        </div>
        <div
          className="kpi-card kpi-amber"
          title="Share of these bookings where the booking feed carries no commission status at all. A blank is MISSING DATA, not evidence of zero commission — the feed reported a status on roughly 18% of consortium bookings when measured on 2026-08-11, so this number describes our visibility, not our yield."
        >
          <span className="kpi-label">Commission not reported</span>
          <span className="kpi-value">{unknownPct}%</span>
          <span className="kpi-sub">
            of bookings carry no commission status
          </span>
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <span className="section-label">Consortium reliance</span>
          <span className="section-meta">
            {loading
              ? 'Refreshing…'
              : `${rows.length.toLocaleString()} of ${(data?.opportunities.length ?? 0).toLocaleString()}`}
          </span>
        </div>
        <div className="section-body">
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              marginBottom: 14,
            }}
          >
            <div style={{ flex: '1 1 280px', maxWidth: 420 }}>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search client, hotel, or chain…"
                className="search-input"
              />
            </div>
            <FilterGroup label="Window">
              {MONTH_OPTIONS.map((m) => (
                <button
                  key={m}
                  onClick={() => setMonths(m)}
                  className={'pill' + (months === m ? ' active' : '')}
                  data-testid={`rate-opportunities-window-${m}`}
                  data-active={months === m ? 'true' : 'false'}
                >
                  {m}mo
                </button>
              ))}
            </FilterGroup>
            <FilterGroup label="Program">
              <button
                onClick={() => setProgram('')}
                className={'pill' + (program === '' ? ' active' : '')}
                data-testid="rate-opportunities-program-all"
                data-active={program === '' ? 'true' : 'false'}
              >
                All
              </button>
              {(['bcd', 'thor', 'virtuoso'] as Program[]).map((p) => (
                <button
                  key={p}
                  onClick={() => setProgram(p)}
                  className={'pill' + (program === p ? ' active' : '')}
                  data-testid={`rate-opportunities-program-${p}`}
                  data-active={program === p ? 'true' : 'false'}
                >
                  {PROGRAM_LABEL[p]}
                </button>
              ))}
            </FilterGroup>
            <FilterGroup label="Min nights">
              {MIN_NIGHTS_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  onClick={() => setMinNights(o.value)}
                  className={'pill' + (minNights === o.value ? ' active' : '')}
                >
                  {o.label}
                </button>
              ))}
            </FilterGroup>
          </div>

          {data?.truncated && (
            <div className="banner banner-warn">
              Showing a partial slice — the booking feed returned the maximum
              rows for this window. Narrow the window or program to see all
              opportunities.
            </div>
          )}
          {error && (
            <div
              className="banner banner-error"
              data-testid="rate-opportunities-error"
            >
              {error}
            </div>
          )}

          <div className="table-scroll">
            <table
              className="data-table"
              data-testid="rate-opportunities-table"
            >
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Hotel</th>
                  <th>Program</th>
                  <th className="right">Reservations</th>
                  <th className="right">Nights</th>
                  <th className="right">ADR</th>
                  <th className="right">Room spend</th>
                  <th>Commission</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr
                    className="empty"
                    data-testid="rate-opportunities-loading"
                  >
                    <td colSpan={8}>Loading opportunities…</td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr className="empty" data-testid="rate-opportunities-empty">
                    <td colSpan={8}>
                      No consortium-rate reliance matches the current filters.
                    </td>
                  </tr>
                )}
                {rows.map((o) => (
                  <tr
                    key={o.client_name + '|' + o.property_key}
                    data-testid="rate-opportunities-row"
                  >
                    <td style={{ fontWeight: 600 }}>{o.client_name}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{o.property_name}</div>
                      <div
                        style={{
                          fontSize: 'var(--fs-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {[o.city, o.state].filter(Boolean).join(', ') ||
                          o.chain ||
                          '—'}
                      </div>
                    </td>
                    <td>
                      <ProgramCell
                        dominant={o.dominant_program}
                        mix={o.program_mix}
                      />
                    </td>
                    <td className="right">{o.reservations.toLocaleString()}</td>
                    <td className="right" style={{ fontWeight: 600 }}>
                      {o.nights.toLocaleString()}
                    </td>
                    <td className="right">
                      {o.adr == null ? '—' : '$' + o.adr.toFixed(0)}
                    </td>
                    <td className="right">{fmtMoneyShort(o.room_spend)}</td>
                    <td>
                      <CommissionCell o={o} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: 'var(--text-muted)',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );
}

function ProgramCell({
  dominant,
  mix,
}: {
  dominant: Program;
  mix: Record<Program, number>;
}) {
  const active = (['bcd', 'thor', 'virtuoso'] as Program[]).filter(
    (p) => mix[p] > 0,
  );
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {active.map((p) => (
        <span
          key={p}
          className={'badge' + (p === dominant ? ' badge-pink' : '')}
          title={`${mix[p]} nights on ${PROGRAM_LABEL[p]}`}
        >
          {PROGRAM_LABEL[p]}
        </span>
      ))}
    </span>
  );
}

function CommissionCell({ o }: { o: Opportunity }) {
  // Consortium bookings mostly carry no commission in the feed — "unknown" is
  // the common case and is itself the finding (we earn nothing here). Never
  // fabricate a yield.
  if (o.commissionable > 0) {
    return (
      <span
        className="badge badge-green"
        title={`${o.commissionable} commissionable`}
      >
        {o.commissionable} comm.
      </span>
    );
  }
  if (o.non_commissionable > 0) {
    return (
      <span
        className="badge badge-red"
        title={`${o.non_commissionable} non-commissionable`}
      >
        Non-comm.
      </span>
    );
  }
  return (
    <span
      style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-xs)' }}
      title="The feed carries no commission status for these bookings. That is missing data, not a zero — same vocabulary as the KPI above."
    >
      Not reported
    </span>
  );
}

function fmtMoneyShort(v: number): string {
  if (!isFinite(v) || v === 0) return '—';
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2) + 'M';
  if (v >= 10_000) return '$' + (v / 1_000).toFixed(0) + 'k';
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
